import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client'
import { AuthContext } from '@deltek/specification-client/core/Configurations/AuthContext'
import { isDeltekUserorAdmin } from '@deltek/specification-client/core/Widgets/ProductListing/productsUtils'
import {
    AddProductDetailNote,
    DeleteProductDetailNote,
    UpdateProductDetailNote,
} from 'bpm/api/graphQL/mutations/productDetailNotes.mutation'
import { GetProductDetailNotes } from 'bpm/api/graphQL/queries/productDetailNotes.queries'
import useWindow from 'bpm/shared/hooks/useWindow'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import useGlobalSnackbar from 'shared/utils/useGlobalSnackbar'
import {
    addNoteData,
    pdnReactiveVar,
    removeNoteData,
    removeTempNote,
    setCurrentUser,
    setListingId,
    setNotes,
    setPDNUserList,
    setTempNotes,
    updateNoteData,
    usersReactiveVar,
} from '../ListingNotes.store'
import { PDNResponseType } from '../ListingNotes.types'

const usePDNhook = () => {
    // ReactiveVar States
    const {
        tempNotes,
        listingId,
        currentUser,
        notes,
        isLoaded,
        count: apiCount,
    } = useReactiveVar(pdnReactiveVar)

    const users = usersReactiveVar()
    const location = useLocation()
    const { getWindowURL, getWindowQuery } = useWindow()

    // States
    const [editableNote, setEditableNote] = useState<string | undefined>(
        undefined
    )
    const [isPDNModalOpen, setIsPDNModalOpen] = useState(false)

    // API Queries
    const { showGlobalSnackbar } = useGlobalSnackbar()

    const currentUserInfoResponse = useContext(AuthContext)

    const [
        getProductDetailNotes,
        { data: pdnAPIData, loading: pdnLoading },
    ] = useLazyQuery(GetProductDetailNotes, {
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            const users = [
                ...new Set(
                    pdnAPIData?.getProductDetailNotes?.result.map(
                        (note: PDNResponseType) => note.metadata.createdBy ?? ''
                    ) as string[]
                ),
            ]

            setPDNUserList(users)
            setNotes(data.getProductDetailNotes, getWindowQuery())
        },
    })

    const [addNote] = useMutation(AddProductDetailNote, {
        onCompleted: (data) => {
            if (
                data.addProductDetailNote &&
                location.pathname.includes(data.addProductDetailNote.listingId)
            ) {
                addNoteData(data.addProductDetailNote)
            }
        },
        onError: () => {
            showGlobalSnackbar({
                variables: {
                    message: 'An error occurred when adding note.',
                    variant: 'error',
                    id: 'Add_Note_Failed',
                },
            })
        },
        fetchPolicy: 'no-cache',
    })

    const [updateNote] = useMutation(UpdateProductDetailNote, {
        onCompleted: (data) => {
            if (Object.keys(data.updateProductDetailNote).length) {
                updateNoteData(data.updateProductDetailNote)
            }
        },
        onError: () => {
            showGlobalSnackbar({
                variables: {
                    message: 'An error occurred when updating note.',
                    variant: 'error',
                    id: 'Update_Note_Failed',
                },
            })
        },
        fetchPolicy: 'no-cache',
    })

    const [deleteNote] = useMutation(DeleteProductDetailNote, {
        onCompleted: (data) => {
            if (data.deleteProductDetailNote.isDeleted) {
                removeNoteData(data.deleteProductDetailNote.id)
            }
        },
        onError: () => {
            showGlobalSnackbar({
                variables: {
                    message: 'An error occurred when deleting note.',
                    variant: 'error',
                    id: 'Delete_Note_Failed',
                },
            })
        },
        fetchPolicy: 'no-cache',
    })

    const { isDeltekUser } = isDeltekUserorAdmin(currentUserInfoResponse)

    const { pdnData } = useMemo(() => {
        const pdnData = [...Object.values(tempNotes), ...Object.values(notes)]
        return { pdnData: pdnLoading ? [] : pdnData }
    }, [tempNotes, notes, pdnLoading])

    const handleSaveNote = useCallback(
        ({
            noteType,
            longDescription,
            isEdit,
            isTemp,
            noteId,
            reason,
            overrideListingId,
        }) => {
            const tempListingId =
                overrideListingId ||
                listingId ||
                location.pathname.split('/product/listing/')[1].split('?')[0]

            if (!!tempListingId) {
                if (!isEdit) {
                    if (
                        [
                            'GENERALNOTE',
                            'COPYMOVE',
                            'DELETEORSTATUSCHANGE',
                        ].includes(noteType) &&
                        !isTemp
                    ) {
                        addNote({
                            variables: {
                                addProductDetailNoteInput: {
                                    content: longDescription,
                                    listingId: tempListingId,
                                    productDetailNoteType: noteType,
                                    reason,
                                },
                            },
                        })
                        return
                    }

                    setTempNotes({ noteType, longDescription, reason })
                } else {
                    if (!isTemp) {
                        updateNote({
                            variables: {
                                productDetailNoteId: noteId,
                                updateProductDetailNoteInput: {
                                    content: longDescription,
                                    listingId: tempListingId,
                                    productDetailNoteType: noteType,
                                    reason,
                                },
                            },
                        })
                    } else {
                        setTempNotes({ noteType, longDescription, reason })
                    }

                    setEditableNote(undefined)
                }
            }
        },
        [addNote, updateNote, editableNote, listingId, location.pathname]
    )

    const handleCancelEditNote = useCallback(() => {
        setEditableNote(undefined)
    }, [])

    const handleEditNote = useCallback(
        (id: string) => {
            setEditableNote(id)
        },
        [setEditableNote]
    )
    const handleDeleteNote = useCallback(
        ({ id, isTemporary }: { id: string; isTemporary: boolean }) => {
            if (!!isTemporary) {
                removeTempNote(id)
                return
            } else {
                deleteNote({
                    variables: {
                        productDetailNoteId: id,
                    },
                })
            }
        },
        [deleteNote]
    )

    const refetchNotes = useCallback(() => {
        const updatedListingId = getWindowURL()
            .split('product/listing/')[1]
            .split('?')[0]

        getProductDetailNotes({
            variables: {
                input: {
                    listingId: updatedListingId,
                    take: 1000,
                },
            },
        })

        setListingId(updatedListingId)
    }, [getWindowURL, getProductDetailNotes, setListingId])

    const fetchNotes = useCallback(
        (listingId) => {
            getProductDetailNotes({
                variables: {
                    input: {
                        listingId: listingId,
                        take: 1000,
                    },
                },
            })
        },
        [getProductDetailNotes]
    )

    useEffect(() => {
        // Set user if not yet set — guard against undefined currentUserInfo (e.g. loading state)
        if (
            !!currentUserInfoResponse &&
            !!currentUserInfoResponse.currentUserInfo &&
            !currentUser.fullName
        ) {
            setCurrentUser(currentUserInfoResponse.currentUserInfo)
        }
    }, [currentUserInfoResponse, currentUser])

    return {
        currentUser,
        editableNote,
        handleEditNote,
        handleCancelEditNote,
        handleDeleteNote,
        handleSaveNote,
        isDeltekUser,
        isPDNModalOpen,
        listingId,
        PDNUserList: users,
        pdnData,
        pdnLoading,
        refetchNotes,
        fetchNotes,
        setIsPDNModalOpen,
        tempNotes,
        totalCount: Number(apiCount) + Number(Object.keys(tempNotes).length),
    }
}

export default usePDNhook
